import { Component } from '@theme/component';

/**
 * One style and one size, chosen together.
 *
 * This is the unit a shopper thinks of as "a pair", and having it own its own state is what
 * removes the question every bug in the old builder came down to: which pair does this
 * element belong to. It belongs to the selection it sits inside.
 *
 * It may hold one step or two — the couples flow puts a style and a size on one step, the
 * single-pack flow splits them across two — and it may hold tabs. None of that changes what
 * it owns, which is why both layouts stopped needing separate handling.
 *
 * Its style and size grids reach it with qualified refs
 * (`ref="product-builder-selection/styles[]"`), so a `tab-group` in between cannot capture
 * them on the way up.
 *
 * A selection can hold several style grids — a step with a Men's tab and a Women's tab has
 * one each — and they are alternatives, not separate picks. Choosing in one clears the
 * others, because the selection still has exactly one style.
 *
 * There is exactly one size grid, whichever category the style came from: a selection has
 * one size, so a second grid would be a second answer to a question asked once. A template
 * carrying more gets the first, and the console says so.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [styles] - Style grids inside this selection.
 * @property {HTMLElement[]} [sizes] - Size grids inside this selection.
 *
 * @extends {Component<Refs>}
 */
class ProductBuilderSelection extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('product-builder-styles:change', this.#handleStyleChange);
    this.addEventListener('product-builder-sizes:change', this.#handleChange);

    // So the size grid starts on a ladder rather than empty. A parent upgrades before its
    // children, so the grids are still plain elements at this point.
    Promise.all(['product-builder-styles', 'product-builder-sizes'].map((tag) => customElements.whenDefined(tag))).then(
      () => this.isConnected && this.#pushStyle()
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('product-builder-styles:change', this.#handleStyleChange);
    this.removeEventListener('product-builder-sizes:change', this.#handleChange);
  }

  /** @type {any} The grid the style on screen came from, so a move between them is visible. */
  #lastStyleGrid = null;

  /** @returns {any[]} Every style grid in this selection — one per category on offer. */
  get styleGrids() {
    return this.refs.styles ?? [];
  }

  /** @returns {any} The grid holding the pick, or the first when nothing is picked yet. */
  get styleGrid() {
    return this.styleGrids.find((grid) => grid.selected) ?? this.styleGrids[0] ?? null;
  }

  /** @returns {any} The one size grid this selection reads. */
  get sizeGrid() {
    const grids = this.refs.sizes ?? [];

    if (grids.length > 1) {
      console.warn('product-builder-selection: one size grid per selection; using the first.', this);
    }

    return grids[0] ?? null;
  }

  /** @returns {HTMLElement | null} The style picked here. */
  get style() {
    return this.styleGrid?.selected ?? null;
  }

  /** @returns {string | null} The size picked here. */
  get size() {
    return this.sizeGrid?.size ?? null;
  }

  /** @returns {boolean} Whether this selection has everything it asks for. */
  get isComplete() {
    if (this.styleGrid && !this.style) return false;
    if (this.sizeGrid && !this.size) return false;

    return true;
  }

  /**
   * Restore a selection named in the url.
   *
   * @param {string | null} style - A style handle.
   * @param {string | null} size - A size label.
   */
  restore(style, size) {
    // Every category: a url may name a style in a tab that is not the one open.
    if (style) {
      for (const grid of this.styleGrids) {
        if (grid.selectByHandle(style)) break;
      }
    }

    // After the style: setting it rebuilds the grid, clearing any size applied first.
    if (size) this.sizeGrid?.selectByLabel(size);
  }

  /**
   * Point the size grid at the style in play, so it builds that ladder.
   *
   * Before anything is picked it takes the size block's own default, or failing that the
   * first category's, rather than standing empty. The block's setting is what a step whose
   * sizes belong to a category other than the first tab needs.
   */
  #pushStyle() {
    const grid = this.sizeGrid;
    if (!grid) return;

    grid.style = this.style ?? grid.defaultStyle ?? this.styleGrids[0]?.fallback ?? null;
  }

  /**
   * One style per selection: the categories are alternatives, so a pick in one clears the
   * rest. Without this a shopper could leave a men's and a women's style both looking
   * chosen, and only one of them would reach the cart.
   *
   * @param {Event} event
   */
  #handleStyleChange = (event) => {
    const picked = /** @type {any} */ (event.target);

    if (/** @type {CustomEvent} */ (event).detail?.tile) {
      // A pick in another category is a new question, not a change of mind — the size grid is
      // told so before it is handed the style, so it starts the ladder empty.
      if (this.#lastStyleGrid && this.#lastStyleGrid !== picked) this.sizeGrid?.forgetSize();
      this.#lastStyleGrid = picked;

      for (const grid of this.styleGrids) {
        if (grid !== picked) grid.clear();
      }
    }

    this.#pushStyle();
    this.#handleChange();

    if (/** @type {CustomEvent} */ (event).detail?.picked) this.#showSizes(picked);
  };

  /**
   * Bring the sizes into view once a shopper picks a style.
   *
   * Only where they share the step, and only while that step is the one on screen: a step
   * that asks for the size next has a continue button to take the shopper there, and a url
   * naming a style for a step nobody is looking at should move nothing.
   *
   * @param {any} picked - The style grid the pick came from.
   */
  #showSizes(picked) {
    const sizes = this.sizeGrid;
    if (!sizes || !picked) return;

    const step = picked.closest('.product-builder__step-panel');
    if (!step || step.hidden || !step.contains(sizes)) return;

    // The continue button sticks to the bottom of the step, so the page ends higher than the
    // window does. Its height, not where it is now: it has yet to reach the bottom edge, and
    // it is a different height on a phone.
    const cta = step.querySelector('.product-builder__cta');
    const sticks = cta ? getComputedStyle(cta).position === 'sticky' : false;
    const floor = window.innerHeight - (sticks ? cta.getBoundingClientRect().height : 0);

    // Already readable without moving: scrolling anyway would shift the page under someone
    // who can see what they are being asked for.
    const gap = parseFloat(getComputedStyle(sizes).scrollMarginBlockEnd) || 0;
    const box = sizes.getBoundingClientRect();
    if (box.top >= 0 && box.bottom + gap <= floor) return;

    // Scrolled by hand, not by `scrollIntoView`: `style` on a size grid is the garment style,
    // so there is no CSSOM object to hang a `scroll-margin` on. Never past the grid's top,
    // which is what a ladder taller than the window ends up asking for.
    const motion = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    window.scrollBy({ top: Math.min(box.bottom + gap - floor, box.top), behavior: motion });
  }

  #handleChange = () => {
    this.dispatchEvent(
      new CustomEvent('product-builder-selection:change', {
        bubbles: true,
        detail: { selection: this, style: this.style, size: this.size, complete: this.isComplete },
      })
    );
  };

}

if (!customElements.get('product-builder-selection')) {
  customElements.define('product-builder-selection', ProductBuilderSelection);
}
